/**
 * 관리 화면 공통 표기·상수(2026-09-15).
 *
 * 서버가 주는 값은 영문 코드(`admin`, `session_closed` …)라 화면 문구로 바꿔 보여야 한다. 화면마다 따로 번역하면
 * 같은 값이 곳마다 다르게 불리므로 **여기 한 곳**에서 관리한다. 모르는 코드는 원문을 그대로 보여 준다
 * (서버에 새 이벤트가 생겨도 화면이 빈칸이 되지 않게 — 각 화면이 `?? code` 로 처리한다).
 */

/** 역할(users.role) — 서버 `USER_ROLE` 과 같은 값(§14.19: 수퍼유저 · 조직 관리자 · 일반 사용자). */
export const ROLE = {
  SU: 'su',
  ADMIN: 'admin',
  USER: 'user',
} as const;

export const ROLE_LABEL: Readonly<Record<string, string>> = {
  [ROLE.SU]: '수퍼유저',
  [ROLE.ADMIN]: '관리자',
  [ROLE.USER]: '사용자',
};

/** 사용자 상태(users.status) — 서버 `ENTITY_STATUS` 와 같은 값. */
export const USER_STATUS = {
  ACTIVE: 'active',
  DISABLED: 'disabled',
} as const;

/** 비밀번호 최소 길이 — 서버 `PASSWORD_MIN_LENGTH`(constants.ts) 와 **같아야 한다**(입력칸에서 미리 막는다). */
export const PASSWORD_MIN_LENGTH = 8;

/** 비밀번호 설정 링크 용도 — 서버 `PASSWORD_TOKEN_PURPOSE` 와 같은 값(§14.19.9). */
export const PASSWORD_LINK_PURPOSE = {
  INVITE: 'invite',
  RESET: 'reset',
} as const;

/**
 * 기기 접근 수준(agent_permissions.permission_type) — §14.19.2 에서 **보기 / 제어 두 가지**로 정리(순서 = 넓어지는 방향).
 * 실제로 쓸 수 있는 기능은 사용자의 기능 권한과 겹치는 것만이다(실효 = 기능 ∩ 기기 수준).
 */
export const PERMISSION_OPTIONS: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: 'view', label: '보기', hint: '화면 보기 + 채팅·그리기·녹화(제어·클립보드·파일 전송은 막힘)' },
  { value: 'control', label: '제어', hint: '사용자에게 허용된 기능을 모두 사용' },
];

/** 새 권한을 줄 때의 기본값 — 원격지원의 일반적인 목적(제어). */
export const DEFAULT_PERMISSION = 'control';

export const PERMISSION_LABEL: Readonly<Record<string, string>> = {
  ...Object.fromEntries(PERMISSION_OPTIONS.map((o) => [o.value, o.label])),
  // 옛 값(이행 전 데이터가 잠깐 보일 수 있다) — 효과가 제어와 같았다.
  admin: '제어',
};

/** 세션 상태(sessions.status). */
export const SESSION_STATUS_LABEL: Readonly<Record<string, string>> = {
  requested: '승인 대기',
  active: '진행 중',
  closed: '종료',
  error: '오류',
};

/** 승인 결과(sessions.approval_status) — 거절·응답 없음은 상태보다 이쪽이 더 많은 것을 말해 준다. */
export const APPROVAL_LABEL: Readonly<Record<string, string>> = {
  approved: '승인',
  rejected: '거절',
  timeout: '응답 없음',
};

/** 감사 로그 이벤트(access_logs.event_type) — 서버 `ACCESS_EVENT` 와 같은 값. */
export const EVENT_LABEL: Readonly<Record<string, string>> = {
  session_requested: '접속 요청',
  session_approved: '접속 승인',
  session_rejected: '접속 거절',
  session_timeout: '승인 응답 없음',
  session_closed: '세션 종료',
  token_reuse_detected: '로그인 토큰 재사용 탐지',
  agent_disabled: '기기 차단',
  agent_enabled: '기기 차단 해제',
  agent_anon_disabled: '코드 접속 차단',
  agent_anon_enabled: '코드 접속 허용',
  agent_renamed: '기기 이름 변경',
  user_created: '사용자 추가',
  user_updated: '사용자 정보 변경',
  user_disabled: '사용자 비활성화',
  user_enabled: '사용자 재활성화',
  user_password_reset: '비밀번호 초기화',
  user_password_changed: '본인 비밀번호 변경',
  user_deleted: '사용자 삭제',
  // 비밀번호 설정 링크(§14.19.9)
  user_invited: '초대 메일',
  user_password_reset_link: '비밀번호 재설정 메일',
  password_reset_requested: '비밀번호 찾기 요청',
  password_set: '비밀번호 설정(링크)',
  // 조직 관리(수퍼유저, §14.19)
  org_created: '조직 생성',
  org_renamed: '조직 이름 변경',
  org_disabled: '조직 정지',
  org_enabled: '조직 재개',
  org_deleted: '조직 삭제',
  org_restored: '조직 되살리기',
  // 동시 세션 상한(§15, 2026-10-23)
  org_session_limit_changed: '조직 세션 상한 변경',
  session_limit_rejected: '세션 상한 거절',
  // 그룹 트리·ID로 추가한 기기(§14.20) — 서버 ACCESS_EVENT 와 같은 키. 화면 문구는 "기기"(2026-10-12 사용자 요청).
  group_created: '그룹 생성',
  group_renamed: '그룹 이름 변경',
  group_moved: '그룹 이동',
  group_deleted: '그룹 삭제',
  agent_group_moved: '기기 그룹 이동',
  org_entry_added: '기기 추가(ID)',
  org_entry_updated: '기기 변경(ID)',
  org_entry_deleted: '기기 삭제(ID)',
};

/**
 * 눈에 띄게 표시할 이벤트 — 보안상 확인이 필요한 것(차단·탐지·삭제 등).
 * 목록에서 이것만 색을 달리해, 긴 로그 속에서도 사람이 먼저 봐야 할 줄을 찾게 한다.
 */
export const NOTABLE_EVENTS: ReadonlySet<string> = new Set([
  'token_reuse_detected',
  'agent_disabled',
  'user_disabled',
  'user_password_reset',
  'user_deleted',
  'org_disabled',
]);

/** 목록 한 쪽 크기 — 서버 상한(세션 200 · 로그 500) 안에서 화면이 한눈에 읽히는 정도. */
export const HISTORY_PAGE_SIZE = 50;
export const LOG_PAGE_SIZE = 50;
